import { useNavigate } from "@tanstack/react-router";
import { onAuthStateChanged, signOut, User } from "firebase/auth";
import { auth } from "firebase.config";
import { useEffect, useRef, useState } from "react";
import {
  ContactRole,
  SerializableUser,
  useOnboardingStore,
  useUserStore
} from "shared/store";

/**
 * Sets the __session cookie with the Firebase ID token.
 * This cookie is used for authentication of browser requests that cannot
 * send Authorization headers (e.g., <img> tags loading documentation images).
 */
const setSessionCookie = (token: string): void => {
  // Set cookie with appropriate security settings
  // SameSite=Strict prevents CSRF attacks
  // Secure ensures cookie is only sent over HTTPS in production
  const isSecure = window.location.protocol === "https:";
  const secureFlag = isSecure ? "; Secure" : "";
  document.cookie = `__session=${token}; Path=/; SameSite=Strict${secureFlag}`;
};

/**
 * Clears the __session cookie on logout.
 */
const clearSessionCookie = (): void => {
  document.cookie = "__session=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT";
};

/**
 * Converts a Firebase User object to a serializable plain object.
 * Firebase User objects contain methods and circular references that can't be JSON serialized.
 */
const serializeUser = (user: User): SerializableUser => ({
  uid: user.uid,
  email: user.email,
  displayName: user.displayName,
  photoURL: user.photoURL,
  emailVerified: user.emailVerified
});

// write function to check if user is authenticated firebase auth and return user data
export const useAuthState = () => {
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const setUser = useUserStore((state) => state.setUser);
  const setRoles = useUserStore((state) => state.setRoles);
  const navigate = useNavigate();
  const hasInitialized = useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setAuthUser(user);

        // Extract roles from Firebase custom claims first
        // We need to check if this is a provisioning user before validating email domain
        try {
          // Force refresh the token to ensure we have the latest claims
          const idTokenResult = await user.getIdTokenResult(true);
          const claims = idTokenResult.claims;

          // Set the session cookie for browser requests (e.g., <img> tags)
          // This allows the hub-api to authenticate image requests
          setSessionCookie(idTokenResult.token);

          // Allow provisioning users (onboarding contacts) regardless of email domain
          // Only check roosterinc.com for non-provisioning users (internal app users)
          const isProvisioningUser = claims.provisioningUser === true;
          if (!isProvisioningUser && !user?.email?.includes("roosterinc.com")) {
            setLoading(false);
            signOut(auth);
            return;
          }

          // For provisioning users, verify we can restore the full session
          // If sessionStorage is missing onboardingUser, we can't restore properly
          // Sign out to force a fresh login
          if (isProvisioningUser) {
            const storedOnboardingUser =
              sessionStorage.getItem("onboardingUser");
            if (!storedOnboardingUser) {
              console.log(
                "[useAuthState] Provisioning user without session data - signing out"
              );
              setLoading(false);
              signOut(auth);
              return;
            }
          }

          // Store serializable user data (not the full Firebase User object)
          setUser(serializeUser(user));

          // Get roles from custom claims (set during user creation in hub-api)
          const userRoles = (claims.roles as ContactRole[]) || [];
          setRoles(userRoles);

          // Populate instructions data from claims and sync to sessionStorage
          if (
            claims.workdayRegion ||
            claims.zoomConnected ||
            claims.targetEnvironment
          ) {
            useOnboardingStore.getState().setInstructionsDataFromClaims(claims);

            const existingData = JSON.parse(
              sessionStorage.getItem("onboardingData") || "{}"
            );
            const updatedData = {
              ...existingData,
              ...(claims.workdayRegion
                ? { workday_region: claims.workdayRegion }
                : {}),
              ...(claims.zoomConnected
                ? { zoom_connected: claims.zoomConnected }
                : {}),
              ...(claims.targetEnvironment
                ? { target_environment: claims.targetEnvironment }
                : {})
            };
            sessionStorage.setItem(
              "onboardingData",
              JSON.stringify(updatedData)
            );
          }
        } catch (error) {
          console.error("Error getting ID token result:", error);
          // Set empty roles on error
          setRoles([]);
        }

        hasInitialized.current = true;
      } else {
        setAuthUser(null);
        // Clear user and roles when user logs out
        setUser(null);
        setRoles([]);
        // Clear the session cookie
        clearSessionCookie();

        // Always clear sessionStorage when user is null (logged out)
        // This prevents re-authentication from stored tokens after explicit logout
        const hadStoredCredentials =
          sessionStorage.getItem("onboardingToken") ||
          sessionStorage.getItem("onboardingUser");

        sessionStorage.removeItem("onboardingToken");
        sessionStorage.removeItem("onboardingUser");

        // Only redirect to login if:
        // 1. We've already initialized (not the first auth state change)
        // 2. OR there were stored credentials that we just cleared
        // 3. AND we're not already on the login page
        const isAlreadyOnLogin = window.location.pathname === "/login";
        if (
          (hasInitialized.current || hadStoredCredentials) &&
          !isAlreadyOnLogin
        ) {
          navigate({
            to: "/login"
          });
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { user: authUser, loading };
};
